import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import compose from 'recompose/compose'
import { showNotification } from 'react-admin'
import { FormSection, reduxForm } from 'redux-form'
import _ from 'lodash'

import { withStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import ClearIcon from '@material-ui/icons/Clear'
import Grid from '@material-ui/core/Grid'
import SendIcon from '@material-ui/icons/Send'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import Typography from '@material-ui/core/Typography'
import CreateEditIcon from '@material-ui/icons/Create'
import FTIcon from '@material-ui/icons/Assignment'

import { renderModelInputs, renderDatasheetInputs } from '../Inputs'


const styles = {
    form: {
        margin: '20px'
    },
    button: {
        icon: {
            marginRight: '10px'
        }
    },
    title : {
        padding: '0 0 20px 20px',
        borderBottom: '1px solid #B5B5B7',
        alignItems: 'center',
        color : '#64676C',
        fontSize : 25,
        fontWeight: 600,
        '& svg' : {
            color : '#33C2D5',
            paddingRight : 10,
            fontSize: 18,
        }
    },
    subTitle : {
        marginTop : 10,
        padding: '0 0 20px 20px',
        display: 'flex',
        alignItems: 'center',
        color : '#64676C',
        fontSize : 18,
        fontWeight: 600,
        '& svg' : {
            color : '#33C2D5',
            paddingRight : 10,
            fontSize: 18,
        }
    },
    inputContainer : {
        marginBottom : 15
    },
    thead : {
        '& tr th' : {
            color: '#019EAC',
            fontSize: '0.95rem',
            fontWeight: 600,
            borderBottom : 'none',
            backgroundColor : '#EFEEEF',
        }
    },
    tbody : {
        '& tr td' : {
            color : '#676767',
            fontSize: '0.95rem',
        },
        '& tr td:first-child' : {
            fontWeight: 500,
        }
    }
}

const Buttons = withStyles(styles)((props) => {
    const { pristine, submitting, invalid, reset, classes, edit } = props
    return (
        <Grid container justify="space-between">
            <Grid item>
                <Button
                    type="submit"
                    disabled={invalid || submitting}
                    color="primary"
                >
                    <SendIcon className={classes.button.icon}/>
                    {edit ? "Guardar" : "Crear"}
                </Button>
            </Grid>
            <Grid item>
                <Button
                    type="button"
                    disabled={pristine || submitting}
                    onClick={reset}
                    color="secondary"
                >
                    <ClearIcon style={classes.button.icon}/>
                    Limpiar
                </Button>
            </Grid>
        </Grid>
    )
})


const Datasheet = withStyles(styles)((props) => {
    const { classes, schema, permissions, initialValues } = props
    const certification = !!schema.properties["ESTADO_CERTIFICACION"]

    const disableDatasheetFields = permissions.role === "externo"
    const disableValidatedDatasheetFields = certification && permissions.role === "coordinado"
    const disableCertificationFields = permissions.role === "coordinado"

    let estadoCertificacion = {}
    if (certification && !!initialValues){
        estadoCertificacion = _.get(initialValues, 'datasheet.ESTADO_CERTIFICACION')
    }

    return (
        <Table className={classes.table}>
            <TableHead className={classes.thead}>
                <TableRow>
                    <TableCell align="center">Dato Técnico</TableCell>
                    <TableCell align="center">Valor</TableCell>
                    {certification && <TableCell align="center">Estado Certificación</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody className={classes.tbody}>
                {renderDatasheetInputs(
                    schema,
                    estadoCertificacion,
                    disableDatasheetFields,
                    disableValidatedDatasheetFields,
                    disableCertificationFields
                )}
            </TableBody>
        </Table>
    )
})

const get_input_width = (input) => {
    const short_input_types = ["number", "date"]
    if(input && short_input_types.includes(input.props.type)){
        return 3
    } else {
        return 6
    }
}


const GenericForm = (props) => {
    const {
        title,
        inputs,
        schema,
        permissions,
        save,
        pristine,
        reset,
        submitting,
        redirect,
        invalid,
        handleSubmit,
        classes,
        edit,
        error,
        showNotification
    } = props

    const buttonProps = {
        pristine,
        submitting,
        invalid,
        reset,
        edit
    }

    if (permissions === null) {
        return null
    }

    if(error){
        showNotification(error, 'warning')
    }

    return (
        <Fragment>
            <Typography className={classes.title} style={!edit ? { marginTop : 20 } : {}}>
                <CreateEditIcon /> {edit ? "Editar" : "Crear"} {title}
            </Typography>
            <form
                onSubmit={handleSubmit((data) => {
                    if(data.inf_id == 0){ data.inf_id = null}
                    if(!data.datasheet){ data.datasheet = {} }
                    return save(data, redirect)
                })}
                className={classes.form}
            >
                <Grid container spacing={24}>
                    <Grid item xs={12}>
                        <Typography className={classes.subTitle}>
                            <FTIcon /> Registros
                        </Typography>
                    </Grid>
                    {_.map(renderModelInputs(inputs, permissions.role), (input, index) => (
                        <Grid
                            item xs={12} md={get_input_width(input)}
                            key={`modelfield-${index}`}
                            className={classes.inputContainer}
                        >
                            {input}
                        </Grid>
                    ))}
                    {!!schema && (
                        <Grid item xs={12} style={{marginTop : 30}}>
                            <Typography className={classes.subTitle}>
                                <FTIcon /> Ficha Técnica
                            </Typography>
                            <FormSection name="datasheet">
                                <Datasheet
                                    schema={schema}
                                    permissions={permissions}
                                    initialValues={props.initialValues}
                                />
                            </FormSection>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Buttons {...buttonProps} />
                    </Grid>
                </Grid>
            </form>
        </Fragment>
    )
}

const enhance = compose(
    connect((state, props) => ({
        form: 'record-form',
        saving: props.submitting,
    })),
    connect(null, {
        showNotification,
    }),
    reduxForm({
        destroyOnUnmount: false,
        enableReinitialize: true,
        keepDirtyOnReinitialize: true,
    }),
    withStyles(styles)
);

export default enhance(GenericForm)
